import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { Banknote, PieChart, Undo2 } from "lucide-react";
import { useState } from "react";
import { getIntervalBarrierDate } from "shared";
import { RecentTransactionDisplay } from "@/components/RecentTransactionDisplay";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heading } from "@/components/ui/text";
import { cn } from "@/lib/clientUtils";
import { trpc } from "@/lib/trpcClient";

const validateSearch = type({
  budget: "string?",
});

export const Route = createFileRoute("/_authed/_org/budgets/$groupId/")({
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc }, params: { groupId } }) => {
    queryClient.ensureQueryData(
      trpc.budget.transactions.queryOptions({
        groupId,
      }),
    );
    queryClient.ensureQueryData(trpc.org.users.queryOptions());
  },
  validateSearch,
});

function RouteComponent() {
  const { groupId } = Route.useParams();
  const { budget: budgetId } = Route.useSearch();

  const { data: budgetGroup } = useSuspenseQuery(
    trpc.budget.get.queryOptions({ groupId }),
  );

  return (
    <div className="flex flex-col gap-8 px-6">
      <BudgetProgressBar budgetGroupId={groupId} />

      <RecentTransactionDisplay
        groupId={budgetId ? undefined : groupId}
        budgetId={budgetId}
        periodStart={
          budgetGroup.refreshInterval
            ? getIntervalBarrierDate(
                budgetGroup.refreshInterval,
                "start",
                new Date(),
              )
            : undefined
        }
      />
    </div>
  );
}

interface BudgetProgressBarProps {
  className?: string;
  showLabels?: boolean;
  defaultView?: "allocation" | "spend";
  budgetGroupId: string;
}

const COLORS = [
  "oklch(0.6 0.2 265)",
  "oklch(0.75 0.18 140)",
  "oklch(0.65 0.22 50)",
  "oklch(0.7 0.2 340)",
  "oklch(0.55 0.18 200)",
  "oklch(0.68 0.2 30)",
  "oklch(0.72 0.16 180)",
  "oklch(0.58 0.22 310)",
];

export function BudgetProgressBar({
  className,
  showLabels = true,
  defaultView = "allocation",
  budgetGroupId,
}: BudgetProgressBarProps) {
  const { data: budgetGroup } = useSuspenseQuery(
    trpc.budget.get.queryOptions({ groupId: budgetGroupId }),
  );
  const { budget } = Route.useSearch();
  const navigate = Route.useNavigate();

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [view, setView] = useState<"allocation" | "spend">(defaultView);

  // Derive selected budget from URL state
  const selectedSubBudget = budget
    ? budgetGroup.subBudgets.find((sb) => sb.id === budget)
    : null;

  const totalBudgetCents = budgetGroup.subBudgets.reduce(
    (sum, sub) => sum + sub.targetCents,
    0,
  );
  const totalSpentCents = budgetGroup.subBudgets.reduce(
    (sum, sub) => sum + sub.balanceCents,
    0,
  );

  const isOverBudget = totalSpentCents > totalBudgetCents;
  const isSingleItem = budgetGroup.subBudgets.length === 1;

  const centsToDollars = (cents: number) => cents / 100;

  const handleSegmentClick = (subBudgetId: string) => {
    if (isSingleItem) return;
    navigate({
      search: (prev) => ({ ...prev, budget: subBudgetId }),
    });
  };

  const handleBackClick = () => {
    navigate({
      search: (prev) => ({ ...prev, budget: undefined }),
    });
  };

  if (selectedSubBudget) {
    const spentAmount = centsToDollars(selectedSubBudget.balanceCents);
    const targetAmount = centsToDollars(selectedSubBudget.targetCents);
    const percentageSpent =
      (selectedSubBudget.balanceCents / selectedSubBudget.targetCents) * 100;
    const remainingAmount = targetAmount - spentAmount;
    const color =
      COLORS[
        budgetGroup.subBudgets.findIndex((s) => s.id === selectedSubBudget.id) %
          COLORS.length
      ];

    return (
      <div className={cn("w-full space-y-3", className)}>
        {/* Sub-budget Title with Close Button */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-foreground text-lg">
              {selectedSubBudget.name}
            </h3>

            {selectedSubBudget.description && (
              <p className="text-muted-foreground text-sm">
                {selectedSubBudget.description}
              </p>
            )}
          </div>
          <button
            onClick={handleBackClick}
            className="text-muted-foreground transition-colors hover:text-foreground"
            type="button"
          >
            <Undo2 className="size-4.5" />
          </button>
        </div>

        <div className="relative h-8 w-full overflow-hidden rounded-lg bg-secondary">
          {/* Spent portion - solid fill */}
          <div
            className="absolute top-0 left-0 h-full transition-all duration-300"
            style={{
              backgroundColor: color,
              width: `${Math.min(percentageSpent, 100)}%`,
            }}
          />
          {/* Unspent portion - striped pattern */}
          {percentageSpent < 100 && (
            <div
              className="absolute top-0 h-full transition-all duration-300"
              style={{
                backgroundColor: color,
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 4px,
                  rgba(0, 0, 0, 0.15) 4px,
                  rgba(0, 0, 0, 0.15) 8px
                )`,
                left: `${percentageSpent}%`,
                opacity: 0.3,
                width: `${100 - percentageSpent}%`,
              }}
            />
          )}
        </div>

        {/* Summary */}
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            <span className="font-medium text-foreground">
              ${spentAmount.toLocaleString()}
            </span>{" "}
            of ${targetAmount.toLocaleString()} spent
          </div>
          <div
            className={cn(
              "font-medium",
              percentageSpent > 100
                ? "text-destructive"
                : "text-muted-foreground",
            )}
          >
            {percentageSpent.toFixed(1)}%
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-card/50 p-4">
          <div>
            <div className="text-muted-foreground text-xs">Spent</div>
            <div className="font-semibold text-foreground text-lg">
              ${spentAmount.toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Remaining</div>
            <div
              className={cn(
                "font-semibold text-lg",
                remainingAmount < 0 ? "text-destructive" : "text-foreground",
              )}
            >
              ${remainingAmount.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const percentageSpent = (totalSpentCents / totalBudgetCents) * 100;

  return (
    <div className={cn("w-full space-y-3", className)}>
      {/* Header with title and dropdown */}
      <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
        <Heading size={"internal"} variant={"internal"}>
          Budget {!isSingleItem && "Group"} Overview
        </Heading>
        {!isSingleItem && (
          <Tabs
            onValueChange={(val) => setView(val as "allocation" | "spend")}
            value={view}
          >
            <TabsList variant="standard">
              <TabsTrigger
                value="allocation"
                variant="standard"
                tooltip="Show allocated spend"
              >
                <div className="flex items-center gap-2">
                  <PieChart className="size-3.5 text-neutral-400" />
                  Allocation
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="spend"
                variant="standard"
                tooltip="Show actual spend"
              >
                <div className="flex items-center gap-2">
                  <Banknote className="size-3.5 text-neutral-400" />
                  Spend
                </div>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {view === "allocation" ? (
        <>
          {/* Allocation View - shows allocated amounts with spent/unspent portions */}
          <div className="relative h-8 w-full overflow-hidden rounded-lg bg-secondary">
            <div className="flex h-full">
              {budgetGroup.subBudgets.map((subBudget, index) => {
                const allocatedPercentage =
                  (subBudget.targetCents / totalBudgetCents) * 100;
                const spentPercentageOfAllocated =
                  (subBudget.balanceCents / subBudget.targetCents) * 100;
                const isHovered = hoveredItem === subBudget.id;
                const color = COLORS[index % COLORS.length];

                return (
                  <button
                    type="button"
                    key={subBudget.id}
                    className={cn(
                      "relative h-full",
                      !isSingleItem && "cursor-pointer",
                    )}
                    style={{
                      width: `${allocatedPercentage}%`,
                    }}
                    onMouseEnter={() => setHoveredItem(subBudget.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => handleSegmentClick(subBudget.id)}
                  >
                    {/* Allocated portion - striped background */}
                    <div
                      className={cn(
                        "absolute inset-0 transition-all duration-200",
                        isHovered
                          ? "brightness-110"
                          : hoveredItem
                            ? "opacity-30 grayscale"
                            : "",
                      )}
                      style={{
                        backgroundColor: color,
                        backgroundImage: `repeating-linear-gradient(
                          45deg,
                          transparent,
                          transparent 4px,
                          rgba(0, 0, 0, 0.15) 4px,
                          rgba(0, 0, 0, 0.15) 8px
                        )`,
                        opacity: hoveredItem && !isHovered ? 0.15 : 0.3,
                      }}
                    />

                    {/* Spent portion - solid fill */}
                    <div
                      className={cn(
                        "absolute top-0 left-0 h-full transition-all duration-200",
                        isHovered
                          ? "brightness-110"
                          : hoveredItem
                            ? "opacity-30 grayscale"
                            : "",
                      )}
                      style={{
                        backgroundColor: color,
                        width: `${Math.min(spentPercentageOfAllocated, 100)}%`,
                      }}
                    />

                    {/* Tooltip */}
                    {isHovered && (
                      <div className="-translate-x-1/2 pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 whitespace-nowrap rounded-md bg-popover px-3 py-2 text-sm shadow-lg ring-1 ring-border">
                        <div className="font-medium text-popover-foreground">
                          {subBudget.name}
                        </div>
                        <div className="text-muted-foreground">
                          <span className="font-medium text-popover-foreground">
                            $
                            {centsToDollars(
                              subBudget.balanceCents,
                            ).toLocaleString()}
                          </span>{" "}
                          spent of{" "}
                          <span className="font-medium text-popover-foreground">
                            $
                            {centsToDollars(
                              subBudget.targetCents,
                            ).toLocaleString()}
                          </span>{" "}
                          allocated
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {spentPercentageOfAllocated.toFixed(1)}% of allocation
                          spent
                        </div>
                        {subBudget.description && (
                          <div className="mt-1 text-muted-foreground text-xs">
                            {subBudget.description}
                          </div>
                        )}
                        {!isSingleItem && (
                          <div className="mt-1 text-muted-foreground/70 text-xs">
                            Click to view details
                          </div>
                        )}
                        {/* Arrow */}
                        <div
                          className="-translate-x-1/2 absolute top-full left-1/2"
                          style={{
                            borderLeft: "6px solid transparent",
                            borderRight: "6px solid transparent",
                            borderTop: "6px solid hsl(var(--popover))",
                            height: 0,
                            width: 0,
                          }}
                        />
                      </div>
                    )}

                    {/* Divider between segments */}
                    {index < budgetGroup.subBudgets.length - 1 && (
                      <div className="absolute top-0 right-0 h-full w-px bg-background/20" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary for allocation view */}
          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              <span className="font-medium text-foreground">
                ${centsToDollars(totalSpentCents).toLocaleString()}
              </span>{" "}
              of ${centsToDollars(totalBudgetCents).toLocaleString()} allocated
            </div>
            <div
              className={cn(
                "font-medium",
                percentageSpent > 100
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {percentageSpent.toFixed(1)}%
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="relative h-8 w-full overflow-hidden rounded-lg bg-secondary">
            <div className="flex h-full">
              {budgetGroup.subBudgets.map((subBudget, index) => {
                const spentPercentage = isOverBudget
                  ? (subBudget.balanceCents / totalSpentCents) * 100
                  : (subBudget.balanceCents / totalBudgetCents) * 100;

                const isHovered = hoveredItem === subBudget.id;
                const color = COLORS[index % COLORS.length];

                return (
                  <button
                    type="button"
                    key={subBudget.id}
                    className={cn(
                      "relative h-full",
                      !isSingleItem && "cursor-pointer",
                    )}
                    style={{
                      width: `${spentPercentage}%`,
                    }}
                    onMouseEnter={() => setHoveredItem(subBudget.id)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => handleSegmentClick(subBudget.id)}
                  >
                    {/* Spent portion - solid fill only */}
                    <div
                      className={cn(
                        "absolute inset-0 transition-all duration-200",
                        isHovered
                          ? "brightness-110"
                          : hoveredItem
                            ? "opacity-30 grayscale"
                            : "",
                      )}
                      style={{
                        backgroundColor: color,
                      }}
                    />

                    {/* Tooltip for spend view */}
                    {isHovered && (
                      <div className="-translate-x-1/2 pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 whitespace-nowrap rounded-md bg-popover px-3 py-2 text-sm shadow-lg ring-1 ring-border">
                        <div className="font-medium text-popover-foreground">
                          {subBudget.name}
                        </div>
                        <div className="text-muted-foreground">
                          <span className="font-medium text-popover-foreground">
                            $
                            {centsToDollars(
                              subBudget.balanceCents,
                            ).toLocaleString()}
                          </span>{" "}
                          spent
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {isOverBudget
                            ? `${((subBudget.balanceCents / totalSpentCents) * 100).toFixed(1)}% of total spend`
                            : `${((subBudget.balanceCents / totalBudgetCents) * 100).toFixed(1)}% of total budget`}
                        </div>
                        {subBudget.description && (
                          <div className="mt-1 text-muted-foreground text-xs">
                            {subBudget.description}
                          </div>
                        )}
                        {!isSingleItem && (
                          <div className="mt-1 text-muted-foreground/70 text-xs">
                            Click to view details
                          </div>
                        )}
                        {/* Arrow */}
                        <div
                          className="-translate-x-1/2 absolute top-full left-1/2"
                          style={{
                            borderLeft: "6px solid transparent",
                            borderRight: "6px solid transparent",
                            borderTop: "6px solid hsl(var(--popover))",
                            height: 0,
                            width: 0,
                          }}
                        />
                      </div>
                    )}

                    {/* Divider between segments */}
                    {index < budgetGroup.subBudgets.length - 1 && (
                      <div className="absolute top-0 right-0 h-full w-px bg-background/20" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="text-muted-foreground">
              <span className="font-medium text-foreground">
                ${centsToDollars(totalSpentCents).toLocaleString()}
              </span>{" "}
              spent
              {!isOverBudget && (
                <>
                  {" "}
                  of{" "}
                  <span className="font-medium text-foreground">
                    ${centsToDollars(totalBudgetCents).toLocaleString()}
                  </span>{" "}
                  allocated
                </>
              )}
            </div>
            <div
              className={cn(
                "font-medium",
                isOverBudget ? "text-destructive" : "text-muted-foreground",
              )}
            >
              {isOverBudget ? "Over budget" : `${percentageSpent.toFixed(1)}%`}
            </div>
          </div>
        </>
      )}

      {showLabels && (
        <div className="flex flex-wrap gap-4">
          {budgetGroup.subBudgets.map((subBudget, index) => {
            const isHovered = hoveredItem === subBudget.id;
            const color = COLORS[index % COLORS.length];
            const spentAmount = centsToDollars(subBudget.balanceCents);
            const allocatedAmount = centsToDollars(subBudget.targetCents);

            return (
              <button
                type="button"
                key={subBudget.id}
                className={cn(
                  "flex items-center gap-2 transition-all duration-200",
                  !isSingleItem && "cursor-pointer",
                  isHovered
                    ? "opacity-100"
                    : hoveredItem
                      ? "opacity-30 grayscale"
                      : "opacity-70 hover:opacity-100",
                )}
                onMouseEnter={() => setHoveredItem(subBudget.id)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleSegmentClick(subBudget.id)}
              >
                <div className="relative h-3 w-3 overflow-hidden rounded-sm">
                  {view === "allocation" ? (
                    <>
                      {/* Striped background for allocated */}
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundColor: color,
                          backgroundImage: `repeating-linear-gradient(
                            45deg,
                            transparent,
                            transparent 2px,
                            rgba(0, 0, 0, 0.15) 2px,
                            rgba(0, 0, 0, 0.15) 4px
                          )`,
                          opacity: 0.3,
                        }}
                      />
                      {/* Solid fill for spent portion */}
                      <div
                        className="absolute top-0 left-0 h-full"
                        style={{
                          backgroundColor: color,
                          width: `${Math.min((subBudget.balanceCents / subBudget.targetCents) * 100, 100)}%`,
                        }}
                      />
                    </>
                  ) : (
                    /* Solid fill for spend view */
                    <div
                      className="absolute inset-0"
                      style={{
                        backgroundColor: color,
                      }}
                    />
                  )}
                </div>
                <div className="text-sm">
                  <span className="font-medium text-foreground">
                    {subBudget.name}
                  </span>
                  <span className="ml-1 text-muted-foreground">
                    {view === "allocation"
                      ? `$${spentAmount.toLocaleString()}/$${allocatedAmount.toLocaleString()}`
                      : `$${spentAmount.toLocaleString()}`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
